/**
 * This file contains helpers for managing cookie consent with Civics' Cookie Control.
 */

/**
 * The name of the CustomEvent fired from the onAccept methods in the CookieControl config.
 * This is the primary method which we use to respond to changes to the users preferences.
 *
 * @type {string}
 */
export const CONSENT_COOKIE_UPDATED_EVENT = "cookie-consent-updated";

/**
 * The name of the cookie that CookieControl sets.
 *
 * @type {string}
 */
export const CONSENT_COOKIE_NAME = "CookieControl";

/**
 * The value CookieControl use to represent a consented category.
 *
 * @type {string}
 */
export const CONSENT_COOKIE_CONSENTED_VALUE = "accepted";

/**
 * Mapping of our consent categories to the IAB TCF purposes.
 *
 * Purposes are stored in the TCData object where each key is the purpose number, and it's value
 * determines the consent status which can be either a Boolean or undefined.
 *
 * { 1: true, 2: true, ... }
 *
 * We alias groups of purposes to make managing consent easier. It's these aliases the CMS users and
 * ourselves use to determine consent.
 */
export const IAB_TCF_CATEGORY_PURPOSE_MAP = {
	analytics: [1, 7, 8, 9, 10, 11],
	marketing: [1, 2, 3, 4],
};

export function isTcfEnabled() {
	let enabled = false;

	try {
		enabled = CookieControl.config().iabCMP;
	} catch (error) {
		// no catch
	}

	return enabled;
}

/**
 * Checks if a particular cookie category has been accepted.
 *
 * If no category is specified it returns true. If we are unable to determine consent for
 * any reason we return false.
 *
 * The consent check method changes depending on whether we're in IAB mode. We detect whether we're in
 * IAB via the config setting within the Civic configuration itself in order to make sure we're in sync
 * with how Civic is configured. The issue however is that the config is updated asynchronously and iabCMP
 * will initially be false until Civic has done its initialization. We could therefore receive a false negative
 * resulting is us performing the wrong consent check and potentially performing actions the user has not
 * consented to. In order to prevent this we use a global variable which we set from within Civics' onLoad
 * configuration property, at this point Civic is fully instantiated are we safe to read consent.
 *
 * @param {string} category - The "name" of the cookie category.
 * @returns {boolean} Whether the cookie has been accepted.
 */
export function getConsentForCookieCategory(category) {
	if (!window.civicLoaded) {
		return false;
	}

	let hasConsent;

	if (isTcfEnabled()) {
		hasConsent = getConsentForCookieCategoryFromTcf(category);
	} else {
		hasConsent = getConsentFromOptionalCookieConfig(category);
	}

	return hasConsent;
}

/**
 * Checks if a particular cookie category has been accepted.
 *
 * If no category is specified it returns true. If we are unable to determine consent for
 * any reason we return false.
 *
 * Note: CookieControl provides a getCategoryConsent method for checking consent however
 * in testing it there can be a delay in it reading the cookie. Debugging reveals that
 * CookieControl.config().optionalCookies can return an empty array if CookieControl hasn't
 * fully instantiated despite the cookie both existing in the browser and CookieControl.getCookie.
 * We therefore read the cookie directly to any issues this causes.
 *
 * @param {string} category - The "name" of the cookie category.
 * @returns {boolean} Whether the cookie has been accepted.
 */
export function getConsentFromOptionalCookieConfig(category) {
	let categoryValue;

	try {
		let consentCookie = CookieControl.getCookie(CONSENT_COOKIE_NAME);
		let decodedCookieValue = JSON.parse(decodeURIComponent(consentCookie));
		categoryValue = decodedCookieValue.optionalCookies[category];
	} catch (error) {
		return false;
	}

	return categoryValue === CONSENT_COOKIE_CONSENTED_VALUE;
}

/**
 * Checks if a particular cookie category has been accepted by reading data from __tcfapi.
 *
 * If no category is specified it returns true. If we are unable to determine consent for
 * any reason we return false.
 *
 * @param {string} category - The "name" of the cookie category.
 * @returns {boolean} Whether the cookie has been accepted.
 */
export function getConsentForCookieCategoryFromTcf(category) {
	if (!category) {
		return true;
	}

	/**
	 * tcfData is a property on the window that we define that contains the current consent TCF data.
	 * This property is updated via ____tcfapi('addEventListener') within the Civic conf.
	 */
	if (typeof tcfData === "undefined") {
		return false;
	}

	let requiredCategoryPurposes = IAB_TCF_CATEGORY_PURPOSE_MAP[category];

	if (requiredCategoryPurposes === "undefined") {
		return false;
	}

	for (let i = 0; i < requiredCategoryPurposes.length; i++) {
		let purposeConsent = tcfData.purpose.consents[requiredCategoryPurposes[i]];

		if (purposeConsent) {
			continue;
		}

		return false;
	}

	return true;
}

/**
 * Given a consent category name, get the categories index from the loaded cookie configuration.
 *
 * Whilst numeric indexes are what Civic expect in their public methods for convince we use
 * category names which are easier to understand. Numeric indexes are also more likely to change
 * if the category order is changed in the configuration.
 *
 * Note: This only works when Civic is not in IAB mode. When in IAB mode the optionalCookies config
 * within Civic has no effect.
 *
 * @param name The category name.
 * @returns {number} The index of the category.
 */
export function getConsentCategoryConfigIndex(name) {
	let index;

	try {
		index = CookieControl.config().optionalCookies.findIndex(
			(category) => category.name === name,
		);
	} catch (error) {
		index = -1;
	}

	return index;
}

/**
 * Updates consent for the given category.
 *
 * Note: This only works when Civic is not in IAB mode. When in IAB mode consent can only be updated by
 * the user via the consent UI.
 *
 * @param {string} name The name of the category to update.
 * @param {boolean} consent The consent value.
 * @returns {true} True if the category was successfully updated, otherwise false.
 */
export function changeConsentForCookieCategory(name, consent = false) {
	let index = getConsentCategoryConfigIndex(name);

	if (index === -1) {
		return;
	}

	try {
		CookieControl.changeCategory(index, consent);
	} catch (error) {
		return false;
	}

	return true;
}
